import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@cuito/ui';
import { locationsApi } from '../api/locations';
import type { LocationView } from '../api/stock';
import { useAuthStore } from '../stores/authStore';
import { readServerMessage } from '../lib/apiError';
import {
  filterLocations,
  isShelvable,
  LOCATION_FILTERS,
  type LocationFilter,
} from '../lib/locationList';

/**
 * 地点列表（`05-ui-design-system.md` §3.2 的「地点」入口）。
 *
 * 门店 / 展会 / 仓库都是 `StockLocation`（`03-data-model.md` §4.2），因此**同一张表**
 * 加一个类型筛选就够——按类型拆三个页面会让「这个地点到底在哪一页」变成记忆负担。
 *
 * **展会行只读**：社团的展会是「该社团某一场展会的库存与账本存档」，由缴费审核通过时
 * 自动创建（`03-data-model.md` §4.2），本阶段不提供展会 CRUD；报名与缴费在
 * `/events/participations`（Phase 8）。
 */
export default function LocationsPage() {
  const canWriteProducts = useAuthStore((s) => s.hasPermission('product:write'));
  const canWriteShop = useAuthStore((s) => s.hasPermission('shop:write'));

  const [locations, setLocations] = useState<LocationView[]>([]);
  const [filter, setFilter] = useState<LocationFilter>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setLocations(await locationsApi.list());
    } catch (e) {
      // 与同阶段其它页面同口径：有些提示必须用后端原话（例如权限/租户相关的具体原因），
      // 前端自己拼一句「加载失败」会把唯一有用的信息丢掉
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = filterLocations(locations, filter);
  // 「一个地点都没有」与「筛选之后没有」是两件事：前者要引导去创建，
  // 后者只要提示换筛选——切到「寄售暂存」时显示「还没有地点」会让人以为数据没了
  const emptyBecauseOfFilter = locations.length > 0 && rows.length === 0;

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">地点</h1>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {LOCATION_FILTERS.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`min-h-[44px] rounded-lg border-2 border-black px-4 font-bold ${
              filter === option.value ? 'bg-black text-white' : 'bg-white text-black active:bg-gray-100'
            }`}
          >
            {option.label}
          </button>
        ))}
        <Button variant="secondary" className="ml-auto" onClick={() => void load()} disabled={loading}>
          刷新
        </Button>
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && rows.length === 0 && (
        <p className="text-gray-500">
          {emptyBecauseOfFilter
            ? `当前筛选（${LOCATION_FILTERS.find((option) => option.value === filter)?.label ?? filter}）下没有地点，切换到「全部」可看到 ${locations.length} 个地点`
            : '还没有地点，先创建门店或仓库'}
        </p>
      )}

      {!loading && !error && rows.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">名称</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">类型</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">地点码</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">坐标底板</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">状态</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((location) => (
              <tr key={location.id}>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{location.name}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {LOCATION_FILTERS.find((option) => option.value === location.type)?.label ??
                    location.type}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{location.code}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {location.shelfRows} × {location.shelfCols}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {location.active ? '启用' : '已停用'}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  <div className="flex gap-3">
                    {canWriteProducts && isShelvable(location.type) && (
                      <Link
                        to={`/locations/${location.id}/shelves`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        货架
                      </Link>
                    )}
                    {canWriteShop && (
                      <Link
                        to={`/locations/${location.id}/settings`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        设置
                      </Link>
                    )}
                    {!isShelvable(location.type) && (
                      <span className="text-gray-500">不支持货架</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
